import React, { ReactNode, useContext } from "react";
import { FaDownload, FaEye } from "react-icons/fa";
import DataTable, { Column, Row } from "components/DataTable";
import { BannerContext } from "components/SideBar";

export interface PayrollListRow extends Row {
    id: number;
    employeeId: number;
    employeeName: string;
    basicPay: string;
    deductions: string;
    netpay: string;
    fromdate: string;
    toDate: string;
    month: string;
    email: string;
}

interface GeneratePayrollListProps {
    rows: PayrollListRow[];
    loading: boolean;
    onSave: (employeeId: number) => void;
    // Opens the full read-only payroll details for the given row id.
    onView: (payrollId: number) => void;
    // Controls (employee select + Generate Form button) rendered on the same
    // row as the table search bar, right-aligned via DataTable's actions slot.
    headerActions?: ReactNode;
}

const GeneratePayrollList: React.FC<GeneratePayrollListProps> = ({
    rows,
    loading,
    onSave,
    onView,
    headerActions,
}) => {
    const formatDateValue = (value: Row[keyof Row]) => {
        const raw = String(value ?? "").trim();
        if (!raw || raw === "-") return "-";

        const asNumber = Number(raw);
        let parsedDate: Date;

        if (Number.isFinite(asNumber)) {
            // Support both seconds and milliseconds epoch values.
            const epochMs = asNumber < 1_000_000_000_000 ? asNumber * 1000 : asNumber;
            parsedDate = new Date(epochMs);
        } else {
            parsedDate = new Date(raw);
        }

        if (Number.isNaN(parsedDate.getTime())) {
            return raw;
        }

        return parsedDate.toLocaleDateString("en-GB");
    };

    const columns: Column[] = [
        { key: "employeeName", label: "Employee Name", bold: true },
        { key: "basicPay", label: "Basic Pay", bold: false },
        { key: "deductions", label: "Deductions", bold: false },
        { key: "netpay", label: "Net Pay", bold: false },
        {
            key: "fromdate",
            label: "From",
            bold: false,
            render: (value) => formatDateValue(value),
        },
        {
            key: "toDate",
            label: "To",
            bold: false,
            render: (value) => formatDateValue(value),
        },
        { key: "month", label: "Month", bold: false },
        {
            key: "action",
            label: "Action",
            excludeExport: true,
            noTruncate: true,
            // className: "min-w-[210px]",
            render: (_value, row) => {
                const typedRow = row as PayrollListRow;

                return (
                    <div className="flex items-center gap-1 whitespace-nowrap">
                        <button
                            type="button"
                            title="View payroll details"
                            aria-label="View payroll details"
                            onClick={() => onView(typedRow.id)}
                            className="inline-flex items-center justify-center p-2 rounded-lg text-blue-600 transition-all hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                        >
                            <FaEye />
                        </button>
                        <button
                            type="button"
                            title="Download payslip"
                            onClick={() => onSave(typedRow.employeeId)}
                            className="inline-flex items-center gap-1 p-2 rounded-lg text-primary text-xs font-medium transition-all hover:bg-primary/10"
                        >
                            <FaDownload />
                            Save
                        </button>
                    </div>
                );
            },
        },
    ];

    const { isBannerVisible } = useContext(BannerContext);
    // Match the shared table height used across the app (see Projects.tsx) so the
    // payroll list is the same height as other listing tables — prevents the
    // layout jump when switching between tabs.
    const dynamicHeightClass = isBannerVisible
        ? "h-[calc(100vh-110px)] min-h-[300px]"
        : "h-[calc(100vh-80px)] min-h-[300px]";

    return (
        <DataTable
            // title="Payroll List"
            data={rows}
            columns={columns}
            loading={loading}
            searchWidth="lg:w-72"
            // Fitted height keeps the table body within the viewport (internal
            // scroll). Without it DataTable falls back to a near-full-viewport
            // default that overflows the page chrome and makes the layout jump
            // ("down then up") when the tab mounts.
            heightClass={dynamicHeightClass}
            // Employee select + Generate Form button share the search row, pinned
            // to the right end (actions slot defaults to `ml-auto`).
            preAddButtons={headerActions}
        />
    );
};

export default GeneratePayrollList;
